import React from "react";
import { useReveal, MarqueeBanner } from "../components/Layout";
import { clients, services } from "../mock";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const Studio = () => {
  useReveal();

  return (
    <div className="pt-24">
      {/* HERO */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-8 pb-16">
        <div className="flex justify-between mono text-[11px] tracking-[0.25em] opacity-60 mb-16">
          <div>[ 02 — STUDIO ]</div>
          <div>ŁÓDŹ / FABRYKA SZTUKI</div>
        </div>
        <h1 className="display text-[15vw] md:text-[12vw] leading-[0.85] rise-in">
          Studio,
          <br />
          <span className="serif italic font-normal">gdzie materia</span>
          <br />
          <span className="text-outline">nabiera duszy</span><span className="text-[#FF4D2E]">.</span>
        </h1>
      </section>

      {/* IMAGE STRIP */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto">
        <div className="reveal grid md:grid-cols-12 gap-6">
          <div className="md:col-span-8 aspect-[16/10] overflow-hidden bg-[#E8E2D3]">
            <img src="https://animuszstudio.com/mediam/images/studio.jpg" alt="Animusz Studio" className="w-full h-full object-cover pf-card:hover:scale-105 transition-transform duration-1000 hover:scale-105" />
          </div>
          <div className="md:col-span-4 flex flex-col justify-between">
            <div>
              <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-4">[ MANIFEST ]</div>
              <p className="serif italic text-2xl md:text-3xl leading-tight">
                „Chcemy reanimować tę szlachetną i piękną technikę, jaką jest animacja poklatkowa.”
              </p>
            </div>
            <div className="mt-8 border-t border-black/20 pt-6 mono text-xs uppercase tracking-widest opacity-70">
              Magdalena Parszewska — założycielka
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT TEXT */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-24">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4 reveal">
            <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-4">[ O NAS ]</div>
            <h2 className="display text-5xl md:text-7xl">Kim jesteśmy.</h2>
          </div>
          <div className="md:col-span-8 space-y-8 text-lg md:text-xl leading-relaxed">
            <p className="reveal">
              <span className="serif italic font-bold">Animusz Studio</span> to studio animacji. Realizuje krótkie filmy w technikach cyfrowych 2D oraz tradycyjną animację lalkową, wycinankową, plastelinową i mieszaną. Chce reanimować tę szlachetną i piękną technikę, jaką jest animacja poklatkowa. Nadaje życie Waszym firmowym brandom i projektom.
            </p>
            <p className="reveal">
              To rzetelne i kompleksowe wykonanie <span className="text-[#FF4D2E] serif italic font-bold">od pomysłu po postprodukcję</span>. Z zaangażowaniem realizuje krótkie formy animowane, teledyski, spoty, elementy na strony www, wizualizacje, filmy edukacyjne i instruktażowe.
            </p>
            <p className="reveal">
              Studio prowadzi <Link to="/warsztaty" className="link-underline text-[#FF4D2E] serif italic font-bold">warsztaty filmowe</Link> w Fabryce Sztuki w Łodzi, gdzie znajduje się jego siedziba. Współpracuje z Wytwórnią Filmów Oświatowych i Muzeum Kinematografii w Łodzi. Uczy miłości do sztuki animacji adeptów małych i dużych, uwielbia odkrywać nieznane talenty.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-32">
        <div className="reveal mb-12">
          <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-4">[ CO ROBIMY ]</div>
          <h2 className="display text-5xl md:text-7xl">Usługi.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-black/20">
          {services.map((s, i) => (
            <div key={s} className={`reveal border-b border-black/20 ${i % 2 === 0 ? "md:border-r" : ""} px-2 md:px-6 py-8 md:py-10 flex items-center justify-between group hover:bg-[#FF4D2E] hover:text-[#F2EEE5] transition-colors duration-500`}>
              <div className="flex items-baseline gap-6">
                <span className="mono text-sm opacity-50">{String(i + 1).padStart(2, "0")}</span>
                <span className="display text-3xl md:text-5xl group-hover:italic transition-all duration-500">{s}</span>
              </div>
              <Sparkles size={22} className="opacity-40 group-hover:opacity-100 group-hover:rotate-12 transition-all duration-500" />
            </div>
          ))}
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="pt-32">
        <div className="px-6 md:px-10 max-w-[1800px] mx-auto mb-8 reveal">
          <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-4">[ ZAUFALI NAM ]</div>
          <h2 className="display text-4xl md:text-6xl">Klienci.</h2>
        </div>
        <MarqueeBanner className="bg-[#0E0E0E] text-[#F2EEE5]">
          {clients.map(c => (
            <React.Fragment key={c}>
              <span className="display text-[5vw]">{c}</span>
              <span className="serif italic text-[#FF4D2E] text-[3vw]">*</span>
            </React.Fragment>
          ))}
        </MarqueeBanner>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-32">
        <div className="border-t border-black/20 pt-16 reveal">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <h3 className="display text-5xl md:text-8xl leading-[0.9]">
                Zapraszamy do <span className="serif italic text-[#FF4D2E]">kontaktu</span>.
              </h3>
            </div>
            <div className="md:col-span-4">
              <Link to="/kontakt" className="group inline-flex items-center gap-3 bg-[#0E0E0E] text-[#F2EEE5] px-6 py-4 hover:bg-[#FF4D2E] transition-colors duration-300">
                <span className="text-lg">Porozmawiajmy</span>
                <ArrowUpRight size={20} className="group-hover:rotate-45 transition-transform duration-500" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Studio;
